import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Calendar,
  Edit3,
  Eye,
  Play,
  Users,
} from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import {
  peerLeaderSessionPrepApi,
  type PeerLeaderPrepPayload,
} from '../../../services/api/peerLeaderSessionPrep';
import type {
  AiPromptSelectionsDoc,
  AiPromptSegmentSelections,
  CoachSessionLeaderNote,
  CoachLeaderNotesScope,
} from '../../../services/api/coachSessionPrep';
import { AISuggestionsBanner } from '../../cohorts/components/sessions/AISuggestionsBanner';
import {
  preSessionReflectionApi,
  type PreSessionReflectionPayload,
  type ReflectionStatus,
} from '../../../services/api/preSessionReflection';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useSlideDeckDoc } from '../../../components/common/slides/useSlideDeckDoc';
import { FeaturesDisabledPage } from '../../FeaturesDisabledPage';
import { SessionDescriptionBlock } from '../../../components/common/SessionDescriptionBlock';
import { FullContentHeader } from '../../../components/layout/FullContentHeader';
import { PeerLeaderContextBanner } from './PeerLeaderContextBanner';
import { PeerLeaderLaunchCard } from './PeerLeaderLaunchCard';
import { PrepStepCard, type StepCompletionStatus } from './PrepStepCard';
import { PeerLeaderReflectionAccordion } from './PeerLeaderReflectionAccordion';
import { useReflectionAutosave } from '../preSessionReflection/useReflectionAutosave';
import { PeerLeaderAgendaSectionList } from './PeerLeaderAgendaSectionList';
import { MarkAsCompletedButton } from './MarkAsCompletedButton';
import { MarkAsReviewedControl } from '../../cohorts/components/sessions/MarkAsReviewedControl';
import { StartSessionButton } from './StartSessionButton';

/**
 * Peer Leader Pre-Session Prep Page (MED-814 / E4.2).
 *
 * Route: `/participant/sessions/:sessionId/prep` (canonical) +
 * `/sessions/:sessionId/prep` (magic-link alias from MED-905).
 *
 * Composition root for the peer-leader prep experience. Loads:
 *   1. The prep payload (session header, agenda, rsvp_status, prep markers,
 *      first-time flag, cm_name) via `peerLeaderSessionPrepApi.getPrep`.
 *   2. The reflection payload (Step 1 data) via `preSessionReflectionApi.get`.
 *
 * Branches on `payload.prep_status`:
 *   - `declined` → redirect to `/participant` (AC1)
 *   - `live`     → "Session is live" alert + back link (AC4)
 *   - `locked`   → "Prep not yet unlocked" alert (AC3)
 *   - `unlocked` → render the full three-step flow
 */
// MED-1126 — presence heartbeat cadence, matching the participant waiting room's
// POLL_INTERVAL_MS (4s) so the peer leader's last_seen_at stays inside the
// server's 10s "present" window while they prep.
const PRESENCE_HEARTBEAT_INTERVAL_MS = 4_000;

/**
 * MED-1217 (AC3/AC7) — the outer "Your Pre-Session Reflection" step is complete
 * when every inner row that is PRESENT for this session is complete. A row that
 * isn't shown does not gate:
 *   • Reflection Questions — complete when status is COMPLETED.
 *   • Reflect on Prior Commitments — present only when a prior commitment exists;
 *     complete when its response is non-empty (AC5 — required this round).
 *   • Reading / Tasks — present only when authored; complete when reviewed.
 *
 * This reads the SERVER payload and is used ONLY for the load-time seed (AC7).
 * Live in-session completeness (which must reflect just-typed, not-yet-flushed
 * values — AC4) is tracked inside PeerLeaderReflectionAccordion.
 */
function allPresentReflectionRowsComplete(
  reflection: PreSessionReflectionPayload
): boolean {
  const questionsComplete = reflection.status === 'COMPLETED';
  const commitmentComplete =
    !reflection.prior_commitment ||
    reflection.prior_commitment.reflection_text.trim().length > 0;
  const readingComplete =
    !reflection.reading_tasks || reflection.reading_tasks_reviewed_at != null;
  return questionsComplete && commitmentComplete && readingComplete;
}

export interface PeerLeaderSessionPrepPageProps {
  /**
   * MED-1092 — DEV-only static preview: seeds the prep + reflection payloads and
   * bypasses the feature-flag gate + fetch so the page renders from in-memory
   * fixtures (`/dev/session-prep/peer`). The dev route has no `:sessionId`, so
   * the mutating handlers (which guard on `sessionId`) no-op. Compiled out of
   * production builds.
   */
  preview?: {
    prepPayload: PeerLeaderPrepPayload;
    reflection: PreSessionReflectionPayload | null;
  };
}

export function PeerLeaderSessionPrepPage({
  preview,
}: PeerLeaderSessionPrepPageProps = {}) {
  const isPreview = !!preview;
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const flagOn = useFeatureFlag('live_session_delivery');

  const [prepPayload, setPrepPayload] = useState<PeerLeaderPrepPayload | null>(
    preview?.prepPayload ?? null
  );
  const [reflection, setReflection] =
    useState<PreSessionReflectionPayload | null>(preview?.reflection ?? null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);
  const [firstTimeBannerDismissed, setFirstTimeBannerDismissed] =
    useState(false);
  const [step1Expanded, setStep1Expanded] = useState(true);
  const [step2Expanded, setStep2Expanded] = useState(false);
  const [step3Expanded, setStep3Expanded] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      // Reflection endpoint may 403 for non-participants; we always fetch prep
      // first and only swallow reflection errors so a missing reflection doesn't
      // block the rest of the page (Step 1 will show "no questions yet").
      const prep = await peerLeaderSessionPrepApi.getPrep(sessionId);
      setPrepPayload(prep);
      if (prep.prep_status === 'declined') {
        navigate('/participant', { replace: true });
        return;
      }
      try {
        const refl = await preSessionReflectionApi.get(sessionId);
        setReflection(refl);
      } catch {
        setReflection(null);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prep');
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!isPreview) void fetchAll();
  }, [fetchAll, isPreview]);

  // MED-1126 — presence heartbeat. The peer leader never enters the participant
  // waiting room (prep → Start → active), so without this their session_attendance
  // last_seen_at goes stale and participants see the leader as "not yet here".
  // Pings a dedicated lightweight endpoint (NOT fetchAll — that would re-load the
  // whole page) to keep the leader present in the room. Gated on the LSD flag (the
  // waiting room only exists behind it). Fire-and-forget; self-heals next tick.
  useEffect(() => {
    if (!sessionId || !flagOn) return;
    const ping = () => {
      void peerLeaderSessionPrepApi.heartbeat(sessionId).catch(() => {});
    };
    ping();
    const intervalId = window.setInterval(ping, PRESENCE_HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [sessionId, flagOn]);

  // ---- Step 1 reflection autosave (AC7 / AC8 / IV5) -------------------------
  const [reflectionStatus, setReflectionStatus] =
    useState<ReflectionStatus | null>(null);
  useEffect(() => {
    if (reflection) setReflectionStatus(reflection.status);
  }, [reflection]);

  const {
    state: autosaveState,
    scheduleSave,
    saveAllPending,
  } = useReflectionAutosave(sessionId, nextStatus =>
    setReflectionStatus(nextStatus)
  );

  // MED-1217 (AC2) — the outer "Your Pre-Session Reflection" step must NEVER
  // collapse as a side effect of the live reflection status. `reflectionStatus`
  // is updated on every debounced autosave (useReflectionAutosave.flushNow),
  // i.e. *during typing*, so the old
  //   useEffect(() => { if (status === 'COMPLETED') setStep1Expanded(false) }, [status])
  // slammed the step shut mid-edit (the participant-side MED-1113 symptom,
  // relocated to this peer-only outer wrapper). It is deliberately removed here.
  // The outer step now collapses only:
  //   • on an explicit completion action, when every present inner row is
  //     complete — driven from the accordion via `handleOuterStepComplete`
  //     (AC3/AC4), and
  //   • on load, seeded collapsed when already complete (AC7, below).
  //
  // AC8 — Step 1 stays expanded by default while unfinished (initial
  // useState(true)); the load-time seed only overrides that when already done.

  // AC7 — seed the outer step collapsed on load when every present inner row is
  // already complete, so a returning peer leader never sees it flash open. Done
  // as a one-time render-phase adjustment (NOT an effect): the step cards only
  // mount once the async reflection fetch resolves, so collapsing in an effect
  // would paint one expanded frame first. Setting state during render makes
  // React re-render before committing to the DOM — no flash. Guarded by
  // `step1Seeded` so it runs exactly once, when the reflection first loads.
  const [step1Seeded, setStep1Seeded] = useState(false);
  if (reflection && !step1Seeded) {
    setStep1Seeded(true);
    if (allPresentReflectionRowsComplete(reflection)) {
      setStep1Expanded(false);
    }
  }

  // AC3/AC4 — collapse the outer step in response to an explicit completion
  // action inside the accordion (Reflection Questions Save / Reading Mark as
  // Reviewed), and only when the accordion has confirmed every present inner row
  // is complete. Never called from a background autosave.
  const handleOuterStepComplete = useCallback(() => {
    setStep1Expanded(false);
  }, []);

  // MED-1049 AC10 — auto-collapse-in-place on completion. The card order is
  // fixed (1 → 5); a completed accordion shrinks to its thin header so the
  // remaining work rises toward the top, with NO re-sorting. Card 2 (reflection)
  // collapses above on COMPLETED; here card 3 collapses once "Mark reviewed" is
  // set and card 4 collapses once practice mode has been visited.
  useEffect(() => {
    if (prepPayload?.prep_state.step2_reviewed_at) setStep2Expanded(false);
  }, [prepPayload?.prep_state.step2_reviewed_at]);
  useEffect(() => {
    if (prepPayload?.prep_state.step3_visited_at) setStep3Expanded(false);
  }, [prepPayload?.prep_state.step3_visited_at]);

  // ---- AC1/AC2 AI prompt selections (optimistic, mirrors coach prep) --------
  const [aiSelections, setAiSelections] = useState<AiPromptSelectionsDoc>({});
  useEffect(() => {
    if (prepPayload?.prep_state.ai_prompt_selections) {
      setAiSelections(prepPayload.prep_state.ai_prompt_selections);
    }
  }, [prepPayload?.prep_state.ai_prompt_selections]);

  const handleSaveAiSelections = useCallback(
    async (
      segmentKey: string,
      next: AiPromptSegmentSelections
    ): Promise<void> => {
      if (!sessionId) return;
      const previous = aiSelections;
      const merged: AiPromptSelectionsDoc = { ...previous, [segmentKey]: next };
      setAiSelections(merged); // optimistic
      try {
        const result = await peerLeaderSessionPrepApi.patchAiSelections(
          sessionId,
          merged
        );
        setAiSelections(result.ai_prompt_selections);
      } catch {
        setAiSelections(previous); // revert
        notifications.show({
          color: 'dangerRed',
          title: 'Could not save selection',
          message: 'Try again in a moment.',
        });
      }
    },
    [sessionId, aiSelections]
  );

  // MED-909 QA — peer leader toggles AI Listening (diverges from AC4's CM-only
  // intent). On success we patch the local session so the AI-surface gate
  // (which requires ai_listening_enabled) reacts immediately without a refetch.
  const handleToggleAiListening = useCallback(
    async (
      next: boolean
    ): Promise<{
      ai_listening_enabled: boolean;
      ai_listening_locked: boolean;
    }> => {
      if (!sessionId) {
        return { ai_listening_enabled: next, ai_listening_locked: false };
      }
      const result = await peerLeaderSessionPrepApi.setAiListening(
        sessionId,
        next
      );
      setPrepPayload(prev =>
        prev
          ? {
              ...prev,
              session: {
                ...prev.session,
                ai_listening_enabled: result.ai_listening_enabled,
                ai_listening_locked: result.ai_listening_locked,
              },
            }
          : prev
      );
      return result;
    },
    [sessionId]
  );

  // ---- MED-1046 (E4.2a) Leader Notes (optimistic, mirrors AI selections) -----
  const [leaderNotes, setLeaderNotes] = useState<CoachSessionLeaderNote[]>([]);
  useEffect(() => {
    if (prepPayload?.leader_notes) {
      setLeaderNotes(prepPayload.leader_notes);
    }
  }, [prepPayload?.leader_notes]);

  // The panel's onSave passes scope/groupId, but a peer leader is single-group
  // so we ignore them and always write scope='all_groups' (the API enforces
  // this too). On success we replace the matching block's note locally so the
  // field reflects the persisted state without a full refetch (AC2).
  //
  // MED-1081 — Leader Notes now autosaves. Let errors propagate so the editor's
  // inline "Save failed — try again" status indicator surfaces them (the
  // standard autosave pattern), rather than a toast on every debounced save.
  const handleSaveLeaderNote = useCallback(
    async (args: {
      blockKey: string;
      scope: CoachLeaderNotesScope;
      groupId: string | null;
      contentHtml: string;
    }): Promise<void> => {
      if (!sessionId) return;
      const saved = await peerLeaderSessionPrepApi.saveLeaderNote(sessionId, {
        block_key: args.blockKey,
        content_html: args.contentHtml,
      });
      setLeaderNotes(prev => {
        const without = prev.filter(
          n => !(n.block_key === args.blockKey && n.scope === 'all_groups')
        );
        return saved ? [...without, saved] : without;
      });
    },
    [sessionId]
  );

  // ── MED-1079 — load the slide-deck PDF once for thumbnail rendering ────────
  // Enabled only when an agenda section actually has associated slides. The
  // converted-PDF endpoint is participant-reachable (same path the participant
  // active-session slide card uses), so the peer leader can load it directly.
  // The loaded doc is threaded into the agenda section list, where each section
  // renders its slides as thumbnails (page-number chips while loading / on
  // failure).
  const hasSlideVisuals =
    prepPayload?.agenda?.sections.some(s => s.visuals.length > 0) ?? false;
  const { doc: slideDeckDoc } = useSlideDeckDoc({
    cohortId: prepPayload?.session.cohort_id,
    sessionId,
    enabled: hasSlideVisuals,
  });

  const handleQuestionChange = (questionId: string, responseText: string) => {
    scheduleSave(questionId, responseText);
  };

  const handleSaveAllReflection = async (
    pending: Array<{ question_id: string; response_text: string }>
  ) => {
    await saveAllPending(pending);
  };

  // MED-1115 — persist the Reading / Tasks reviewed marker, then update the
  // reflection payload so the shared card shows the "Reviewed" check + collapses.
  // In preview (no sessionId) optimistically stamp the marker locally.
  const handleMarkReadingReviewed = async () => {
    if (isPreview || !sessionId) {
      setReflection(prev =>
        prev
          ? { ...prev, reading_tasks_reviewed_at: new Date().toISOString() }
          : prev
      );
      return;
    }
    const result = await preSessionReflectionApi.setReadingTasksReviewed(
      sessionId,
      true
    );
    setReflection(prev =>
      prev
        ? {
            ...prev,
            reading_tasks_reviewed_at: result.reading_tasks_reviewed_at,
          }
        : prev
    );
  };

  // ---- Derived state --------------------------------------------------------

  const session = prepPayload?.session ?? null;

  const sessionDateLabel = useMemo(() => {
    if (!session?.scheduled_start) return 'your scheduled date';
    const d = new Date(session.scheduled_start);
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }, [session?.scheduled_start]);

  const timingBadge = useMemo(() => {
    if (!session?.scheduled_start) return null;
    const now = Date.now();
    const start = new Date(session.scheduled_start).getTime();
    if (Number.isNaN(start)) return null;
    const diffMs = start - now;
    if (diffMs <= 0) return 'Now';
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 60) return `In ${diffMin} min`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `In ${diffHr} hour${diffHr === 1 ? '' : 's'}`;
    const diffD = Math.round(diffHr / 24);
    if (diffD === 0) return 'Today';
    if (diffD === 1) return 'In 1 day';
    return `In ${diffD} days`;
  }, [session?.scheduled_start]);

  const step1Status: StepCompletionStatus = useMemo(() => {
    if (!reflection) return 'NOT_STARTED';
    if (reflectionStatus === 'COMPLETED') return 'COMPLETED';
    if (reflectionStatus === 'IN_PROGRESS') return 'IN_PROGRESS';
    return 'NOT_STARTED';
  }, [reflection, reflectionStatus]);

  const step2Status: StepCompletionStatus = prepPayload?.prep_state
    .step2_reviewed_at
    ? 'COMPLETED'
    : 'NOT_STARTED';
  const step3Status: StepCompletionStatus = prepPayload?.prep_state
    .step3_visited_at
    ? 'COMPLETED'
    : 'NOT_STARTED';

  const handleStep2Toggle = async () => {
    if (!sessionId || !prepPayload) return;
    const next = !prepPayload.prep_state.step2_reviewed_at;
    try {
      const updated = await peerLeaderSessionPrepApi.setStep2Reviewed(
        sessionId,
        next
      );
      setPrepPayload(updated);
    } catch (err) {
      notifications.show({
        color: 'dangerRed',
        title: 'Could not update',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const handleStep3Open = async () => {
    if (!sessionId || !prepPayload) return;
    // Fire-and-forget the visited marker; navigation happens regardless so a
    // transient error doesn't block the peer leader from previewing.
    void peerLeaderSessionPrepApi
      .setStep3Visited(sessionId)
      .then(updated => setPrepPayload(updated))
      .catch(() => undefined);
    // MED-873's preview page renders under whichever layout matches the URL
    // prefix. The participant-context route (this one) keeps the peer leader
    // in ParticipantLayout chrome; the page itself detects the prefix and
    // routes Close Preview back to the participant prep page.
    navigate(
      `/participant/cohorts/${prepPayload.session.cohort_id}/sessions/${sessionId}/preview`
    );
  };

  const handleRsvpUpdated = (status: 'confirmed' | 'declined') => {
    if (status === 'declined') {
      navigate('/participant', { replace: true });
      return;
    }
    void fetchAll();
  };

  const handleMarkedCompleted = () => {
    void fetchAll();
  };

  const handleDeclinedFromIntercept = () => {
    navigate('/participant', { replace: true });
  };

  // ---- Render branches ------------------------------------------------------

  // MED-1180 — defense-in-depth: also disable when the per-cohort LSD flag is
  // false on the returned payload (backend already 404s a non-LSD cohort).
  if (!isPreview && (!flagOn || prepPayload?.live_session_delivery_enabled === false))
    return <FeaturesDisabledPage />;

  if (loading) {
    return (
      <Center mih={400}>
        <Loader />
      </Center>
    );
  }

  if (error || !prepPayload) {
    return (
      <Container size="md" pt="xl">
        <Alert
          color="dangerRed"
          icon={<AlertTriangle size={16} />}
          title="Could not load prep"
        >
          {error ?? 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  // AC4 — Session has launched; prep view no longer accessible.
  if (prepPayload.prep_status === 'live') {
    return (
      <Container size="md" pt="xl">
        <Alert
          color="warningAmber"
          icon={<AlertTriangle size={16} />}
          title="Session is live"
        >
          <Stack gap="sm">
            <Text size="sm">
              The prep view is no longer accessible — your session has started.
            </Text>
            <Anchor
              onClick={() => navigate('/participant')}
              style={{ cursor: 'pointer' }}
            >
              Back to your portal
            </Anchor>
          </Stack>
        </Alert>
      </Container>
    );
  }

  // MED-1062 (E4.0h): the session was a no-show ('missed') and is pending
  // reschedule. Show a full "Session pending reschedule" state — a clean amber
  // alert + back link, mirroring the live/locked branches — with no Start
  // affordance. Reachable via the prep email magic-link or a stale tab after
  // the no-show sweep flips the session to 'missed'.
  if (prepPayload.prep_status === 'missed') {
    return (
      <Container size="md" pt="xl">
        <Alert
          color="warningAmber"
          icon={<Calendar size={16} />}
          title="Session pending reschedule"
        >
          <Stack gap="sm">
            <Text size="sm">
              This session was missed and hasn't been rescheduled yet. Your
              cohort manager will set a new time — your prep so far is saved and
              will be here when it's rescheduled.
            </Text>
            <Anchor
              onClick={() => navigate('/participant')}
              style={{ cursor: 'pointer' }}
            >
              Back to your portal
            </Anchor>
          </Stack>
        </Alert>
      </Container>
    );
  }

  // AC3 — Prep not yet unlocked (preceding session hasn't completed).
  if (prepPayload.prep_status === 'locked') {
    return (
      <Container size="md" pt="xl">
        <Stack gap="md">
          <Group gap="xs">
            <Button
              variant="subtle"
              leftSection={<ArrowLeft size={14} />}
              onClick={() => navigate('/participant')}
            >
              Back
            </Button>
          </Group>
          <Alert color="gray" title="Prep not yet unlocked">
            {prepPayload.prep_locked_reason ??
              'Your prep unlocks when the preceding session completes.'}
          </Alert>
        </Stack>
      </Container>
    );
  }

  // ---- Happy path: prep_status === 'unlocked' -------------------------------

  const showFirstTimeBanner =
    prepPayload.is_first_time_leader && !firstTimeBannerDismissed;
  // MED-909 QA — two-layer AI gate (diverges from AC4 + the coach prep behavior
  // per Ben's QA, 2026-06-18):
  //   • aiFeaturesEnabled (modality flag) gates the AI Listening card itself —
  //     always shown when AI features are on, so the peer leader can turn
  //     listening on/off (otherwise there'd be no surface to enable it).
  //   • aiSurfacesEnabled additionally requires ai_listening_enabled — gates the
  //     suggestions banner, per-step AI, and the Sentiment & Guidance panel, so
  //     when listening is OFF those AI surfaces are absent.
  const aiFeaturesEnabled = Boolean(session?.ai_features_enabled);
  const aiSurfacesEnabled =
    aiFeaturesEnabled && Boolean(session?.ai_listening_enabled);

  return (
    <>
      {/* MED-1092 — shared page header (FullContentHeader), matching the coach
          prep page. Peer-only bits live in the slots: the timing + "You're
          leading" badges inline with the title; Preview + Start in the band
          accessory. Sticky to match the coach prep header. */}
      <FullContentHeader
        data-testid="peer-prep-header"
        sticky
        title={
          <Group gap="sm" align="center" wrap="nowrap">
            <Text fz={24} fw={700} c="#1A1A1A" lh={1.1}>
              {session?.title ?? 'Your session'}
            </Text>
            {timingBadge && (
              <Badge
                color="blue"
                variant="light"
                size="md"
                leftSection={<Calendar size={12} />}
              >
                {timingBadge}
              </Badge>
            )}
            <Badge
              color="medleyGreen"
              variant="light"
              size="md"
              leftSection={<Users size={12} />}
            >
              You&apos;re leading
            </Badge>
          </Group>
        }
        programName={session?.program_name}
        clientName={session?.organization_name}
        durationMinutes={session?.duration_minutes}
        onBack={() => navigate('/participant')}
        accessoryPlacement="band"
        accessory={
          <Group gap="sm" wrap="nowrap">
            <Button
              variant="default"
              leftSection={<Eye size={14} />}
              onClick={() => void handleStep3Open()}
            >
              Preview
            </Button>
            <StartSessionButton
              sessionId={sessionId ?? ''}
              cohortId={session?.cohort_id ?? ''}
              scheduledStart={session?.scheduled_start ?? null}
              sessionStatus={session?.status ?? 'scheduled'}
              groupName={session?.group_name ?? null}
              aiFeaturesEnabled={session?.ai_features_enabled ?? false}
            />
          </Group>
        }
      />
      <Container size="xl" py="lg">
        <Stack gap="lg">
          {/* AC12 first-time strong-prompt banner */}
          {showFirstTimeBanner && (
            <Alert
              color="medleyGreen"
              withCloseButton
              onClose={() => setFirstTimeBannerDismissed(true)}
              title="Before your first session"
            >
              Take a few minutes to walk through every step below — this is the
              most important preparation you can do before facilitating.
            </Alert>
          )}

          {/* MED-1049 Card 1 — single RSVP-state context banner. Replaces the
            prior DualRoleBanner + RsvpConfirmationCard pair (AC3): one banner,
            swinging on rsvp_status. `unconfirmed` shows the RSVP request,
            `confirmed` shows the dual-role message with a decline affordance. */}
          {session && (
            <PeerLeaderContextBanner
              sessionId={session.id}
              sessionNumber={session.session_number}
              sessionDateLabel={sessionDateLabel}
              cmName={prepPayload.cm_name}
              rsvpStatus={prepPayload.rsvp_status}
              onRsvpUpdated={handleRsvpUpdated}
            />
          )}

          {/* MED-1101 (AC2/AC3/AC5) — Session Description at the top of the
            content area, below the peer-led status banner (PeerLeaderContextBanner)
            and above the prep step cards. Hidden when unset (AC9). Sourced from
            the already-loaded reflection payload (session_number_config.description,
            AC7) — a peer leader is always enrolled, so the reflection GET resolves. */}
          <SessionDescriptionBlock description={reflection?.description} />

          {/* MED-1049 Card 2 — "Your Pre-Session Reflection". Holds the
            participant pre-session content in the SAME order it renders on the
            participant page: Commitment Review → Reflection → Reading / Tasks.
            MED-1217 — the outer step now collapses only on an explicit
            completion action once EVERY present inner row is complete (the
            accordion owns that gate); the old "collapse live when reflection
            status is COMPLETED" trigger was removed because it fired mid-typing. */}
          <PrepStepCard
            icon={<Edit3 size={20} color="#6B7280" />}
            title="Your Pre-Session Reflection"
            status={step1Status}
            expanded={step1Expanded}
            onToggleExpanded={() => setStep1Expanded(v => !v)}
          >
            {reflection && reflection.questions.length > 0 ? (
              // MED-1115 follow-up — peer-only nested accordion mirroring the
              // Session Guide: Commitment Review → Reflection Questions →
              // Reading / Tasks as numbered, collapsible rows. The participant
              // page keeps the shared cards; this peer treatment is bespoke so
              // the rows don't duplicate this step's "Your Pre-Session
              // Reflection" title (the questions row is titled "Reflection
              // Questions").
              <PeerLeaderReflectionAccordion
                sessionId={sessionId}
                reflection={reflection}
                reflectionStatus={reflectionStatus ?? reflection.status}
                saveHint={autosaveState === 'saving' ? 'Saving…' : undefined}
                onQuestionChange={handleQuestionChange}
                onSaveAllReflection={handleSaveAllReflection}
                onMarkReadingReviewed={handleMarkReadingReviewed}
                onOuterStepComplete={handleOuterStepComplete}
              />
            ) : (
              <Text size="sm" c="dimmed">
                Your reflection prompts haven&apos;t been published yet — check
                back closer to the session.
              </Text>
            )}
          </PrepStepCard>

          {/* MED-1049 Card 3 — Session Guide (was Step 2). Leader Prompts +
            read-only Participant Prompt + slides + per-section Leader Notes
            (MED-1046) with a header "Mark reviewed" control (AC8). */}
          <PrepStepCard
            icon={<BookOpen size={20} color="#6B7280" />}
            title="Session Guide"
            status={step2Status}
            expanded={step2Expanded}
            onToggleExpanded={() => setStep2Expanded(v => !v)}
            headerActions={
              // MED-1092 — reuse the coach Mark-as-reviewed control so the
              // affordance matches the coach prep page. Both onMark/onClear call
              // the same toggle (handleStep2Toggle flips on current state).
              <MarkAsReviewedControl
                markedAt={prepPayload.prep_state.step2_reviewed_at}
                onMark={handleStep2Toggle}
                onClear={handleStep2Toggle}
              />
            }
          >
            {prepPayload.agenda?.is_incomplete ? (
              <Alert color="gray" title="Prep not yet ready">
                Session content is still being prepared — prep unlocks once
                it&apos;s complete.
              </Alert>
            ) : prepPayload.agenda ? (
              <Stack gap="md">
                {/* AC1 — AI suggestions banner (no prep-sheet PDF for peer leaders, AC5).
                  Gated on aiSurfacesEnabled so it's absent when listening is OFF. */}
                {aiSurfacesEnabled && session && (
                  <AISuggestionsBanner
                    sessionId={session.id}
                    selections={aiSelections}
                    showPrepSheet={false}
                  />
                )}
                <PeerLeaderAgendaSectionList
                  sections={prepPayload.agenda.sections}
                  slideDeckDoc={slideDeckDoc}
                  aiContext={
                    aiSurfacesEnabled && session
                      ? {
                          sessionId: session.id,
                          groupId: session.group_id ?? undefined,
                          selections: aiSelections,
                          onSaveSelections: handleSaveAiSelections,
                        }
                      : undefined
                  }
                  /* MED-1046 (E4.2a) — Leader Notes are independent of AI (AC5),
                   so they are always wired regardless of aiSurfacesEnabled. */
                  leaderNotesContext={{
                    notes: leaderNotes,
                    onSave: handleSaveLeaderNote,
                  }}
                />
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Session content isn&apos;t available for this session yet.
              </Text>
            )}
          </PrepStepCard>

          {/* MED-1049 Card 4 — Practice mode (was Step 3). Unchanged. */}
          <PrepStepCard
            icon={<Play size={20} color="#6B7280" />}
            title="Practice mode"
            status={step3Status}
            expanded={step3Expanded}
            onToggleExpanded={() => setStep3Expanded(v => !v)}
          >
            <Stack gap="sm">
              <Text size="sm">
                Open the live session screen in practice mode to walk through
                every section, try the controls, and get comfortable before you
                facilitate. No participants are affected.
              </Text>
              <Box>
                <Button
                  color="medleyGreen"
                  variant="default"
                  leftSection={<Eye size={14} />}
                  onClick={() => void handleStep3Open()}
                >
                  Open practice mode
                </Button>
              </Box>
            </Stack>
          </PrepStepCard>

          {/* MED-1049 Card 5 — Launch / group card. Mirrors the coach prep group
            card: status + Start button, roster, AI Listening then Session
            Details, Sentiment & Guidance when listening is on. The coach
            "Before you start" task list is intentionally not shown (AC9). The
            group/session-details come from the reflection payload already
            loaded above. */}
          {session && (
            <PeerLeaderLaunchCard
              sessionId={session.id}
              cohortId={session.cohort_id}
              groupId={session.group_id}
              groupName={session.group_name}
              scheduledStart={session.scheduled_start}
              sessionStatus={session.status}
              aiFeaturesEnabled={aiFeaturesEnabled}
              aiSurfacesEnabled={aiSurfacesEnabled}
              aiListeningEnabled={session.ai_listening_enabled}
              aiListeningLocked={session.ai_listening_locked}
              onToggleAiListening={handleToggleAiListening}
              group={reflection?.group ?? null}
              sessionDetails={reflection?.session_details ?? null}
            />
          )}

          {/* AC13 Mark as Completed (with AC13 RSVP intercept if unconfirmed) */}
          {session && (
            <Group justify="flex-end">
              <MarkAsCompletedButton
                sessionId={session.id}
                sessionNumber={session.session_number}
                sessionDateLabel={sessionDateLabel}
                cmName={prepPayload.cm_name}
                rsvpStatus={prepPayload.rsvp_status}
                markedCompletedAt={prepPayload.prep_state.marked_completed_at}
                onMarkedCompleted={handleMarkedCompleted}
                onDeclined={handleDeclinedFromIntercept}
              />
            </Group>
          )}
        </Stack>
      </Container>
    </>
  );
}

export default PeerLeaderSessionPrepPage;
